import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, X, Mail, Copy, Check, MessageCircle, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { adminRetentionRemind } from '../services/api';

const DEFAULT_SUBJECT = 'Személyes üzenet — Orosz Coaching';

/**
 * 3 quick-templates. Each is a function of the client context; all placeholders
 * are already substituted so the coach can hit Send instantly.
 *   ctx = { first, inactiveDays, remaining, daysToExpiry, portalUrl }
 */
const TEMPLATES = [
  {
    id:      'friendly',
    label:   'Baráti',
    subject: 'Szia, minden ok?',
    render: (c) =>
`Szia ${c.first}!

Hogy vagy? Rég nem láttalak — már ${c.inactiveDays} napja nem jártál nálam edzeni. Minden rendben, csak sok most a meló, vagy elakadtál valamiben?

Ha kedved van, ugorj be egy edzésre, ${c.portalUrl}-n tudsz időpontot foglalni.

Bármikor keress, ha kérdés van!
Gábor`,
  },
  {
    id:      'motivational',
    label:   'Motivációs',
    subject: 'Ne engedd el a lendületet!',
    render: (c) =>
`Szia ${c.first}!

Feltűnt, hogy már ${c.inactiveDays} napja nem edzettünk együtt. Ne engedd el a lendületet — pont ez az a pont, ahol a legtöbben feladják, és pont ez az, amit átugorva tényleges eredmény lesz.

Van még ${c.remaining} alkalmad a bérletedből, foglalj egy időpontot most, mielőtt eltolódik:
${c.portalUrl}

Számítok rád!
Gábor`,
  },
  {
    id:      'expiring',
    label:   'Lejárat-fókuszú',
    subject: 'A bérleted hamarosan lejár',
    render: (c) => {
      const expBlock = (c.daysToExpiry !== null && c.daysToExpiry !== undefined)
        ? `A bérleted ${c.daysToExpiry} nap múlva lejár, és még ${c.remaining} alkalom van rajta.`
        : `A bérleteden még ${c.remaining} alkalom van, ne hagyd elveszni.`;
      return (
`Szia ${c.first}!

${expBlock} Ne hagyd elveszni — foglalj egy időpontot, van pár jó szabad slot a jövő héten:
${c.portalUrl}

Ha valami akadályoz, szólj bátran, közösen megoldjuk.
Gábor`);
    },
  },
];

const buildTemplateContext = ({ row, portalUrl }) => {
  const first = (row?.name || row?.email || '').trim().split(/[\s@]+/)[0] || 'Barátom';
  return {
    first,
    inactiveDays: Number.isFinite(row?.inactive_days) && row.inactive_days > 0 ? row.inactive_days : 10,
    remaining:    Number.isFinite(row?.remaining_sessions) ? row.remaining_sessions : 0,
    daysToExpiry: Number.isFinite(row?.days_until_expiry) ? row.days_until_expiry : null,
    portalUrl,
  };
};

const RetentionReminderModal = ({ open, onClose, adminKey, row, onSent }) => {
  const portalUrl = useMemo(() => {
    const site = (typeof window !== 'undefined' && window.location?.origin) || 'https://oroszcoaching.hu';
    return `${site.replace(/\/$/, '')}/fiokom`;
  }, []);

  const [subject, setSubject]     = useState(DEFAULT_SUBJECT);
  const [message, setMessage]     = useState('');
  const [templateId, setTemplateId] = useState('friendly');
  const [busy, setBusy]           = useState(null); // 'email' | 'clipboard' | null
  const [copied, setCopied]       = useState(false);

  const applyTemplate = (tid) => {
    const tpl = TEMPLATES.find((t) => t.id === tid) || TEMPLATES[0];
    const ctx = buildTemplateContext({ row, portalUrl });
    setTemplateId(tpl.id);
    setSubject(tpl.subject);
    setMessage(tpl.render(ctx));
  };

  useEffect(() => {
    if (open && row) {
      applyTemplate('friendly');
      setCopied(false);
      setBusy(null);
    }
  }, [open, row, portalUrl]);

  if (!open || !row) return null;

  const send = async (channel) => {
    setBusy(channel);
    try {
      if (channel === 'clipboard') {
        try {
          await navigator.clipboard.writeText(message);
        } catch (_) {
          // Fallback: legacy execCommand
          const ta = document.createElement('textarea');
          ta.value = message;
          document.body.appendChild(ta); ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        setCopied(true);
      }
      const res = await adminRetentionRemind(adminKey, {
        passId:  row.pass_id,
        channel,
        subject: channel === 'email' ? subject : null,
        message,
      });
      if (channel === 'email') {
        toast.success('Emlékeztető elküldve e-mailben.');
      } else {
        toast.success('Szöveg a vágólapon — beillesztheted Messenger/Viber-be.');
      }
      onSent && onSent({ channel, result: res });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sikertelen művelet.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="retention-reminder-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-2xl bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl text-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div>
            <div className="text-sm font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Személyes emlékeztető
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {row.name || row.email} <span className="text-gray-600">·</span> {row.email}
              {Number.isFinite(row.inactive_days) && (
                <> <span className="text-gray-600">·</span> {row.inactive_days} napja inaktív</>
              )}
            </div>
          </div>
          <button
            data-testid="reminder-close-btn"
            onClick={onClose}
            disabled={!!busy}
            className="text-gray-500 hover:text-white transition-colors p-1 rounded"
            aria-label="Bezárás"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
              <Zap size={11} className="text-yellow-500" /> Gyors sablonok
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="reminder-template-picker">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`reminder-template-${t.id}`}
                  onClick={() => applyTemplate(t.id)}
                  disabled={!!busy}
                  className={
                    'text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ' +
                    (templateId === t.id
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700')
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1.5 block">
              Tárgy (csak e-mailhez)
            </label>
            <Input
              data-testid="reminder-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-gray-950 border-gray-700 text-white"
              placeholder="Személyes üzenet — Orosz Coaching"
              disabled={!!busy}
              maxLength={200}
            />
          </div>

          <div>
            <label className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1.5 block">
              Üzenet
            </label>
            <Textarea
              data-testid="reminder-message-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-gray-950 border-gray-700 text-white min-h-[220px] font-mono text-[13px] leading-relaxed"
              disabled={!!busy}
              maxLength={2000}
            />
            <div className="text-[11px] text-gray-500 mt-1 flex justify-between">
              <span>Szerkesztheted mielőtt kiküldöd — a portál link és az inaktív napok már ki vannak töltve.</span>
              <span>{message.length}/2000</span>
            </div>
          </div>

          <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 text-xs text-blue-200 flex gap-2">
            <MessageCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              Ha e-mailben küldöd, az üzenet Orosz Coaching branded HTML template-ben megy ki.
              Ha a vágólapra másolod, tisztán a szöveget kapod meg (Messenger/Viber-hez).
              Bármelyiket választod, az ügyfél 7 napig nem jelenik meg újra a listában.
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-800 bg-gray-950/40 rounded-b-2xl flex-wrap">
          <Button
            data-testid="reminder-cancel-btn"
            variant="ghost"
            onClick={onClose}
            disabled={!!busy}
            className="text-gray-400 hover:text-white"
          >
            Mégse
          </Button>
          <Button
            data-testid="reminder-copy-btn"
            variant="outline"
            onClick={() => send('clipboard')}
            disabled={!!busy || message.trim().length < 10}
            className="border-gray-700 text-gray-100 hover:bg-gray-800"
          >
            {busy === 'clipboard' ? (
              <><Loader2 className="animate-spin mr-1.5" size={14} /> Másolás…</>
            ) : copied ? (
              <><Check className="mr-1.5" size={14} /> Másolva</>
            ) : (
              <><Copy className="mr-1.5" size={14} /> Másolás vágólapra</>
            )}
          </Button>
          <Button
            data-testid="reminder-send-email-btn"
            onClick={() => send('email')}
            disabled={!!busy || message.trim().length < 10}
            className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
          >
            {busy === 'email' ? (
              <><Loader2 className="animate-spin mr-1.5" size={14} /> Küldés…</>
            ) : (
              <><Mail className="mr-1.5" size={14} /> Küldés e-mailben</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RetentionReminderModal;
